import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";

import { prettyDateSimpleISO } from "@shared/lib/time";

interface Balance {
  monthlyCreditsRemaining: number;
  monthlyCreditAllocation: number;
  addonSubscriptionCreditsRemaining: number;
  addonCreditsRemaining: number;
  totalCreditsRemaining: number;
  currentPeriodEndAt: string | null;
  subscriptionPeriodEndAt: string | null;
}

const TONES = {
  plan: { label: "Monthly plan credits", getDescription: (resetText: string) => `Included with your plan · ${resetText}, unused credits do not roll over` },
  subscription: { label: "Subscription credits", getDescription: (resetText: string) => `From your AI credit subscription · ${resetText}, unused credits roll over` },
  topup: { label: "Top-up credits", getDescription: () => "One-time purchases · roll over, used after monthly and subscription credits run out" },
};

const AiCreditBalance: React.FC<{ balance: Balance }> = ({ balance }) => {
  const planResetDate = balance.currentPeriodEndAt ? prettyDateSimpleISO(balance.currentPeriodEndAt) : "next period";
  const planResetText = `resets ${planResetDate}`;
  const subscriptionResetDate = balance.subscriptionPeriodEndAt ? prettyDateSimpleISO(balance.subscriptionPeriodEndAt) : "next period";
  const subscriptionResetText = `resets ${subscriptionResetDate}`;

  const plan = balance.monthlyCreditsRemaining;
  const subscription = balance.addonSubscriptionCreditsRemaining;
  const topUp = balance.addonCreditsRemaining;
  const total = Math.max(plan + subscription + topUp, 1);

  return (
    <Styled.Wrapper>
      <Styled.Bar>
        <Styled.Segment tone="plan" style={{ width: `${(plan / total) * 100}%` }} />
        <Styled.Segment tone="subscription" style={{ width: `${(subscription / total) * 100}%` }} />
        <Styled.Segment tone="topup" style={{ width: `${(topUp / total) * 100}%` }} />
      </Styled.Bar>

      <Styled.Sources>
        <Styled.Source>
          <Styled.SourceRow>
            <Styled.SourceLeft>
              <Styled.Chip tone="plan" />
              <Styled.SourceName>{TONES.plan.label}</Styled.SourceName>
            </Styled.SourceLeft>
            <Styled.SourceAmount>{plan.toLocaleString()}</Styled.SourceAmount>
          </Styled.SourceRow>
          <Styled.SourceDescription>{TONES.plan.getDescription(planResetText)}</Styled.SourceDescription>
        </Styled.Source>

        <Styled.Source>
          <Styled.SourceRow>
            <Styled.SourceLeft>
              <Styled.Chip tone="subscription" />
              <Styled.SourceName>{TONES.subscription.label}</Styled.SourceName>
            </Styled.SourceLeft>
            <Styled.SourceAmount>{subscription.toLocaleString()}</Styled.SourceAmount>
          </Styled.SourceRow>
          <Styled.SourceDescription>{balance.subscriptionPeriodEndAt ? TONES.subscription.getDescription(subscriptionResetText) : "No active subscription"}</Styled.SourceDescription>
        </Styled.Source>

        <Styled.Source>
          <Styled.SourceRow>
            <Styled.SourceLeft>
              <Styled.Chip tone="topup" />
              <Styled.SourceName>{TONES.topup.label}</Styled.SourceName>
            </Styled.SourceLeft>
            <Styled.SourceAmount>{topUp.toLocaleString()}</Styled.SourceAmount>
          </Styled.SourceRow>
          <Styled.SourceDescription>{TONES.topup.getDescription()}</Styled.SourceDescription>
        </Styled.Source>
      </Styled.Sources>
    </Styled.Wrapper>
  );
};

export default AiCreditBalance;

/* Styled Components
======================================================= */
let Styled: any;
Styled = {};

const toneColors = (t: any, tone: string) => {
  const map: any = {
    plan: t.dark ? t.color.gray[600] : t.color.gray[300],
    subscription: t.dark ? t.color.gray[400] : t.color.gray[400],
    topup: t.dark ? t.color.gray[100] : t.color.black,
  };
  return map[tone];
};

Styled.Wrapper = styled.div`
  label: AiCreditBalance;
  max-width: 696px;
`;

Styled.Bar = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: AiCreditBalance_Bar;
    display: flex;
    height: 10px;
    width: 100%;
    overflow: hidden;
    ${[t.rounded.sm]}
    background: ${t.dark ? t.color.gray[800] : t.color.gray[200]};
  `;
});

Styled.Segment = styled.span((props: any) => {
  const t: any = props.theme;
  return css`
    label: AiCreditBalance_Segment;
    height: 100%;
    background: ${toneColors(t, props.tone)};
  `;
});

Styled.Sources = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: AiCreditBalance_Sources;
    ${[t.mt(5), t.rounded.md]}
    border: 1px solid ${t.dark ? t.color.gray[800] : t.color.gray[200]};
  `;
});

Styled.Source = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: AiCreditBalance_Source;
    ${[t.p(5)]}

    & + & {
      border-top: 1px solid ${t.dark ? t.color.gray[800] : t.color.gray[200]};
    }
  `;
});

Styled.SourceRow = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: AiCreditBalance_SourceRow;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: ${t.spacing[3]};
  `;
});

Styled.SourceLeft = styled.span((props: any) => {
  const t: any = props.theme;
  return css`
    label: AiCreditBalance_SourceLeft;
    display: flex;
    align-items: center;
    gap: ${t.spacing[3]};
    min-width: 0;
  `;
});

Styled.Chip = styled.span((props: any) => {
  const t: any = props.theme;
  return css`
    label: AiCreditBalance_Chip;
    flex: 0 0 auto;
    width: 11px;
    height: 11px;
    position: relative;
    top: -2px;
    ${[t.rounded.xs]}
    background: ${toneColors(t, props.tone)};
  `;
});

Styled.SourceName = styled.span((props: any) => {
  const t: any = props.theme;
  return css`
    label: AiCreditBalance_SourceName;
    line-height: 1.1;
    ${[t.text.base, t.text.semibold]}
    color: ${t.dark ? t.color.gray[100] : t.color.gray[900]};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `;
});

Styled.SourceAmount = styled.span((props: any) => {
  const t: any = props.theme;
  return css`
    label: AiCreditBalance_SourceAmount;
    ${[t.text.base, t.text.semibold]}
    color: ${t.dark ? t.color.gray[100] : t.color.black};
    font-variant-numeric: tabular-nums;
  `;
});

Styled.SourceDescription = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: AiCreditBalance_SourceDescription;
    ${[t.ml(6), t.mt(2), t.text.sm]}
    color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
  `;
});
